import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
    addClubEventGuest,
    removeClubEventGuest,
    syncClubEventGuests,
} from '../api/clubEvents';
import { MeetingGuestAddModal } from './MeetingGuestAddModal';
import { MeetingGuestEditModal } from './MeetingGuestEditModal';

/**
 * @typedef {import('../api/clubEvents').ClubEventGuest} ClubEventGuest
 */

/**
 * @param {{
 *   eventId: string,
 *   eventTitle: string,
 *   authToken: string,
 *   guests: ClubEventGuest[],
 *   disabled?: boolean,
 *   onUpdated: () => Promise<void>,
 *   onBusyChange?: (busy: boolean) => void,
 * }} props
 */
export function ClubEventGuestManagePanel({
    eventId,
    eventTitle,
    authToken,
    guests,
    disabled = false,
    onUpdated,
    onBusyChange,
}) {
    const { t } = useTranslation();
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [removingGuestId, setRemovingGuestId] = useState(
        /** @type {string | null} */ (null),
    );
    const [addError, setAddError] = useState(
        /** @type {string | null} */ (null),
    );
    const [editError, setEditError] = useState(
        /** @type {string | null} */ (null),
    );

    const internalBusy =
        addSubmitting || editSubmitting || removingGuestId != null;
    const actionDisabled = disabled || internalBusy;

    useEffect(() => {
        onBusyChange?.(internalBusy);
    }, [internalBusy, onBusyChange]);

    async function handleAddGuest(guest) {
        setAddSubmitting(true);
        setAddError(null);

        try {
            await addClubEventGuest(authToken, eventId, guest);
            await onUpdated();
            toast.success(t('eventCalendarPage.addGuestSuccess'));
            setAddModalOpen(false);
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.addGuestErrorGeneric');
            setAddError(msg);
            throw err;
        } finally {
            setAddSubmitting(false);
        }
    }

    async function handleRemoveGuest(guestId) {
        setRemovingGuestId(guestId);

        try {
            await removeClubEventGuest(authToken, eventId, guestId);
            await onUpdated();
            toast.success(t('eventCalendarPage.removeGuestSuccess'));
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.removeGuestErrorGeneric');
            toast.error(msg);
        } finally {
            setRemovingGuestId(null);
        }
    }

    async function handleUpdateGuests(updatedGuests) {
        setEditSubmitting(true);
        setEditError(null);

        try {
            await syncClubEventGuests(
                authToken,
                eventId,
                guests,
                updatedGuests,
            );
            await onUpdated();
            toast.success(t('eventCalendarPage.updateGuestsSuccess'));
            setEditModalOpen(false);
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.updateGuestsErrorGeneric');
            setEditError(msg);
            throw err;
        } finally {
            setEditSubmitting(false);
        }
    }

    return (
        <>
            <section
                className="event-calendar-guest-manage mt-4 pt-3"
                aria-label={t('eventCalendarPage.manageGuestsTitle')}
            >
                <div className="event-calendar-guest-manage-head d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <h3 className="event-calendar-detail-section-title h6 mb-0">
                        {t('eventCalendarPage.manageGuestsTitle')}
                    </h3>
                    <div className="d-flex flex-wrap gap-2">
                        {guests.length > 0 ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                onClick={() => {
                                    setEditError(null);
                                    setEditModalOpen(true);
                                }}
                                disabled={actionDisabled}
                            >
                                <Pencil size={14} strokeWidth={2} aria-hidden />
                                {t('eventCalendarPage.editGuests')}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                            onClick={() => {
                                setAddError(null);
                                setAddModalOpen(true);
                            }}
                            disabled={actionDisabled}
                        >
                            <Plus size={14} strokeWidth={2} aria-hidden />
                            {t('eventCalendarPage.addGuest')}
                        </button>
                    </div>
                </div>

                {guests.length === 0 ? (
                    <p className="event-calendar-guest-manage-empty small text-secondary mb-0">
                        {t('eventCalendarPage.manageGuestsEmpty')}
                    </p>
                ) : (
                    <ul className="event-calendar-guest-manage-list list-unstyled mb-0">
                        {guests.map((guest, index) => (
                            <li
                                key={guest.id}
                                className="event-calendar-guest-manage-item"
                            >
                                <div className="min-w-0">
                                    <span className="event-calendar-guest-manage-name d-block">
                                        {guest.fullName ||
                                            `${guest.firstName} ${guest.lastName}`.trim()}
                                    </span>
                                    {guest.phoneNumber ? (
                                        <span className="event-calendar-guest-manage-phone small text-secondary d-block">
                                            {guest.phoneNumber}
                                        </span>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 flex-shrink-0"
                                    onClick={() => handleRemoveGuest(guest.id)}
                                    disabled={actionDisabled}
                                    aria-label={t(
                                        'eventCalendarPage.removeGuestAria',
                                        { index: index + 1 },
                                    )}
                                >
                                    <Trash2
                                        size={14}
                                        strokeWidth={2}
                                        aria-hidden
                                    />
                                    {removingGuestId === guest.id
                                        ? t(
                                              'eventCalendarPage.removeGuestSubmitting',
                                          )
                                        : t('eventCalendarPage.removeGuest')}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <MeetingGuestAddModal
                open={addModalOpen}
                meetingTitle={eventTitle}
                submitting={addSubmitting}
                submitError={addError}
                onClose={() => {
                    if (addSubmitting) return;
                    setAddModalOpen(false);
                    setAddError(null);
                }}
                onSubmit={handleAddGuest}
            />

            <MeetingGuestEditModal
                open={editModalOpen}
                guests={guests}
                meetingTitle={eventTitle}
                submitting={editSubmitting}
                submitError={editError}
                onClose={() => {
                    if (editSubmitting) return;
                    setEditModalOpen(false);
                    setEditError(null);
                }}
                onSubmit={handleUpdateGuests}
            />
        </>
    );
}
